import { groupBy } from "ramda";
import { getFilenameInfo, isFilenameOfType, splitPath } from "widgets/utils";

import { IndexStatus, type ProjectFile } from "@/graphql.js";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.js";
import { ContextualFileFilter, type FileSuggestionsContextProps } from "@/modules/files/stores/file-search.ts";
import { FILE_BROWSE_TYPES } from "@/settings/settings.js";

/* --- Utilities --- */
export function getLastFilePathElement(path: string) {
  // Unfortunately, this may break if filename contains a slash.
  const paths = splitPath(path);

  if (!paths.length) {
    return "/";
  }

  return paths[paths.length - 1];
}

export function getParentPathFromProjectFile(fileData: BrowsingProjectFileFragment): string | null {
  return getParentPath(fileData.fullPath);
}

export function getParentPath(fullPath: string | null): string | null {
  if (!fullPath) {
    return null;
  }
  const paths: string[] = splitPath(fullPath);
  if (paths.length < 2) {
    return "/";
  }
  paths.pop();
  return paths.join("/");
}

export function isBrowseable(type?: FILE_BROWSE_TYPES): boolean {
  return [FILE_BROWSE_TYPES.PROJECT, FILE_BROWSE_TYPES.PROJECT_GROUP, FILE_BROWSE_TYPES.DIRECTORY].includes(type);
}

export const isFileOfType =
  (type: string | string[]) =>
  (file?: Pick<ProjectFile, "fileName" | "fileBrowseType" | "url"> | null): boolean => {
    if (!type) {
      // same behavior as within isFileNameOfType(...)
      return true;
    }
    // REGULAR_FILE are the files we see in the file browser. We should not care about
    // all the other types, e.g. UNKNOWN, as it seems we never receive them. However,
    // the type is not always fetched, so we use the empty URL field as a fallback
    // to exclude links. See SEL10-500 and SEL10-2086. Since we are using GraphQL types
    // everywhere it is hard to spot whether every required property is fetched or not
    return (
      (file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE || !file?.url) && isFilenameOfType(type)(file.fileName)
    );
  };

export const isBamFile = isFileOfType("BAM");
export const isBedFile = isFileOfType("BED");
export const isBamOrSamFile = isFileOfType(["BAM", "SAM"]);
export const isAnalysisSetFile = isFileOfType(["GDA", "ABS", "REL"]);
export const isDatasetFile = isFileOfType(["ABS", "REL", "MABS", "MREL", "GDA"]);
export const isAlignmentFile = isFileOfType("MSA");
export const isFastaFile = isFileOfType(["FASTA", "FNA", "FAA"]);
export const isFastQFile = isFileOfType("FASTQ");
export const isGenomeViewerFile = isFileOfType(["BAM", "BED"]);
export const isIndexRequiredForGenomeViewer = isFileOfType(["BAM"]);

/** ----------------------------------------------- **/

export function removeTrailingSlash(path: string) {
  if (path?.endsWith("/") || path?.endsWith("\\")) {
    return path.slice(0, path.length - 1);
  }
  return path;
}

export function getHumanReadableIndexingStatus(status: string | null): string | null {
  switch (status) {
    case IndexStatus.Exists:
      return "Index files exist.";
    case IndexStatus.Indexing:
      return "Index files are currently being created.";
    case IndexStatus.Missing:
      return "Index files are missing.";
    case IndexStatus.Cancelled:
      return "Indexing was cancelled.";
    case IndexStatus.Failed:
      return "Indexing has failed.";
    default:
      return null;
  }
}

export function isFileAccessible(file: ProjectFile) {
  return !file.deleted && !file.hidden;
}

export function isProjectFileWithoutExtension(fileBrowseType?: string) {
  return (
    (fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.DIRECTORY ||
    (fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.HYPERLINK
  );
}

export const getFileInfo = (filename: string, fileBrowseType: string): { baseName: string; extension: string } => {
  if (isProjectFileWithoutExtension(fileBrowseType)) {
    return { baseName: filename, extension: "" };
  }
  const fileNameInfo = getFilenameInfo(filename);
  return {
    baseName:
      filename?.lastIndexOf(".") > -1
        ? filename.substring(0, filename.lastIndexOf("." + fileNameInfo?.extension))
        : filename,
    extension:
      filename?.lastIndexOf(".") > -1
        ? filename.substring(filename.lastIndexOf("." + fileNameInfo?.extension), filename.length)
        : "",
  };
};

export const sortProjectFileByFileName: (files: ProjectFile[]) => ProjectFile[] = (files) =>
  files.sort((file_1, file_2) =>
    (file_1.fileName ?? file_1.fullPath).localeCompare(file_2.fileName ?? file_2.fullPath, "en", {
      sensitivity: "base",
      ignorePunctuation: true,
    }),
  );

/**
 * Take a list of files (expected two), build the basename for each and group the files by identical basename.
 * The basename of a file is all of the original file without the paring information.
 * The name pattern:
 *    (path/)preamble_READ_postamble.extensions
 *    READ is the last group of (R1|R2|1|2)
 *    # -> 1 | 2
 R -> R1 | R2
 * Files are grouped by this base:
 *    (path/)preamble_._postamble.extensions
 *    (path/)preamble_#_postamble.extensions
 *
 * Example of a name pattern and file pair:
 *     guid/guid/dir/asdf_1_wer_R1_xxx.fastq.gz
 *     guid/guid/dir/asdf_1_wer_R2_xxx.fastq.gz
 *  Not part of the pair, because of the different base name:
 *     guid/guid/dir/asdf_2_wer_R2_xxx.fastq.gz
 *
 * @param files
 */
export function getGroupedNearPathsBeforeLastUnderscore(files: ProjectFile[]): ProjectFile[][] {
  const groups = groupBy(getPristinePathForNgsSequencingFiles)(files ?? []);
  const groupsArray = Object.values(groups);
  return groupsArray.map(sortProjectFileByFileName);
}

/**
 * Accept paired end files like
 * 1) sample_1.fastq.gz
 * 2) sample_1_abc.fastq.gz
 * 3) sample_1_100k.fastq.gz
 * 4) sample_R1.fastq.gz
 * 5) sample_R1_abc.fastq.gz
 * 6) sample_R1_100k.fastq.gz
 * All these filenames map to 'sample' which is the part before the paring information.
 *
 *   "preamble_R1_post.fastq.gz",   "preamble_R_post.fastq.gz"    R1 & R2
 *   "preamble_1_post.fastq.gz",    "preamble_1_post.fastq.gz"     1 & 2
 *
 *   "preamble_R1_post.fastq.gz",   "preamble_R.fastq.gz"         R1 & R2
 *   "preamble_1_post.fastq.gz",    "preamble_1.fastq.gz"          1 & 2
 *
 * The following pattern is currently not recognized: sample_1abx.fastq.gz.
 * @param  file
 * @return grouping name
 */
export function getPristinePathForNgsSequencingFiles(file: ProjectFile): string {
  const basename = file.fileName;
  const baseNameMatch = basename?.match(/(?<base>.*)(?<extension>\.(fq|fastq)(\.gz)?)$/);
  if (!baseNameMatch) {
    return file.fullPath;
  }
  const parentPath = getParentPathFromProjectFile(file);
  const fileNameWithoutEnding = baseNameMatch.groups.base;
  const parts: string[] = fileNameWithoutEnding.split("_");

  for (let i = parts.length - 1; i >= 0; i--) {
    const g = parts[i].match(/^(?<pairing>R?[12])$/);
    if (g) {
      const entries: string[] = parts.slice(0, i);
      entries.push(g.groups?.pairing.startsWith("R") ? "R" : "#");
      parts.slice(i + 1, parts.length).every((part) => entries.push(part));
      entries.push(baseNameMatch.groups.extension);
      return `${parentPath ? `${parentPath}/` : ""}${entries.join("_")}`; /// return basename
    }
  }

  return file.fullPath;
}

export function computeContextualFilterIds(
  suggestionsContext?: Partial<FileSuggestionsContextProps>,
  activeContextualFilter?: ContextualFileFilter | null,
) {
  const genomeIds =
    activeContextualFilter === ContextualFileFilter.ASSOCIATED_GENOMES
      ? suggestionsContext?.organisms?.map((o) => o.id)
      : null;
  const eventIds =
    activeContextualFilter === ContextualFileFilter.ASSOCIATED_EVENTS
      ? suggestionsContext?.events?.map((e) => e.id)
      : null;
  const sampleIds =
    activeContextualFilter === ContextualFileFilter.ASSOCIATED_SAMPLES
      ? suggestionsContext?.samples?.map((s) => s.id)
      : null;
  const parentIds =
    activeContextualFilter === ContextualFileFilter.ASSOCIATED_PARENTS
      ? (suggestionsContext?.parents?.map((p) => p.id) as unknown as number[])
      : null;
  return {
    genomeIds,
    eventIds,
    sampleIds,
    parentIds,
  };
}
